import { useEffect, useState, useMemo } from 'react';
import { collection, collectionGroup, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useMarket, filterByPortals } from '../../contexts/MarketContext';
import { ProgressRing } from '../../components/charts';
import { CATEGORY_LABELS, CATEGORY_STYLES, CATEGORY_DOT } from '../../utils/statuses';

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

// Days from today until departure (negative once the tour has left).
function daysUntil(str) {
  const [y, m, d] = str.split('-').map(Number);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(y, m - 1, d) - today) / 86400000);
}

// Outstanding forms turn red inside 3 weeks of departure, amber inside 6.
function awaitingColor(awaiting, days) {
  if (!awaiting || days < 0) return 'text-navy';
  if (days <= 21) return 'text-[#C0392B]';
  if (days <= 42) return 'text-[#E8A020]';
  return 'text-navy';
}

function tourName(r) {
  return r.internalRef ? (r.tourName || `${r.sport} — ${r.destination}`) : (r.tourName || `${r.sport} — ${r.destination}`);
}

export default function ParticipantsOverview() {
  const navigate = useNavigate();
  const { portals } = useMarket();
  const [requests, setRequests] = useState([]);
  const [participantCounts, setParticipantCounts] = useState({}); // requestId -> count
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'requests'), where('archived', '==', false));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        .filter((r) => !r.crmOrigin || r.crmFullyConverted));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collectionGroup(db, 'participants'));
    const unsub = onSnapshot(q, (snap) => {
      const counts = {};
      snap.docs.forEach((d) => {
        const requestId = d.ref.parent.parent.id;
        counts[requestId] = (counts[requestId] || 0) + 1;
      });
      setParticipantCounts(counts);
    });
    return unsub;
  }, []);

  const rows = useMemo(() => {
    const scoped = filterByPortals(requests, portals)
      .filter((r) => (r.category || 'sales_admin') !== 'cancelled' && r.departureDate);
    return scoped
      .map((r) => {
        const players = Number(r.participants) || 0;
        const supporters = (Number(r.supportersMale) || 0) + (Number(r.supportersFemale) || 0);
        const custStaff = Number(r.customerStaff) || 0;
        const expected = players + supporters + custStaff;
        const registered = participantCounts[r.id] || 0;
        const pct = expected > 0 ? Math.min(Math.round((registered / expected) * 100), 100) : 0;
        // null when no group size is set yet, so it shows "—" rather than 0
        const awaiting = expected > 0 ? Math.max(expected - registered, 0) : null;
        return { request: r, expected, registered, awaiting, pct, days: daysUntil(r.departureDate) };
      })
      .filter((row) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return tourName(row.request).toLowerCase().includes(term) || (row.request.destination || '').toLowerCase().includes(term);
      })
      .sort((a, b) => (a.request.departureDate || '').localeCompare(b.request.departureDate || ''));
  }, [requests, participantCounts, portals, search]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[23px] font-extrabold text-navy tracking-[-0.4px]">Participant Registration Overview</h1>
          <p className="text-[13px] text-[#6B7280] mt-1">Every active tour's participant registration progress, in one view.</p>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tours…"
          className="w-[220px] border border-[#E5E7EB] rounded-[9px] px-3 py-[7px] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
        />
      </div>

      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#F1F3F6] bg-surface">
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px]">Tour</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px]">Departure</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] text-center">Participants Registered</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] text-center">Awaiting Completion</th>
                <th className="px-5 py-3 text-[11px] font-bold text-[#6B7280] uppercase tracking-[0.7px] text-center">% Registered</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">No active tours with a departure date yet.</td></tr>
              ) : (
                rows.map(({ request: r, expected, registered, awaiting, pct, days }) => (
                  <tr
                    key={r.id}
                    onClick={() => navigate(`/staff/requests/${r.id}`)}
                    className="border-b border-[#F1F3F6] last:border-0 hover:bg-[#F9FAFB] cursor-pointer transition"
                  >
                    <td className="px-5 py-3">
                      <p className="text-[13.5px] font-semibold text-[#111827]">{tourName(r)}</p>
                      <span className={`inline-flex items-center gap-1 mt-1 px-[7px] py-[2px] rounded-full text-[10px] font-bold ${CATEGORY_STYLES[r.category] || 'bg-[#F3F4F6] text-[#6B7280]'}`}>
                        <span className={`w-[5px] h-[5px] rounded-full ${CATEGORY_DOT[r.category] || 'bg-[#9CA3AF]'}`} />
                        {CATEGORY_LABELS[r.category] || CATEGORY_LABELS.sales_admin}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[13px] text-[#374151] tabular-nums">{formatDate(r.departureDate)}</td>
                    <td className="px-5 py-3 text-center text-[14px] font-bold text-navy tabular-nums">
                      {registered}{expected > 0 ? <span className="text-[#9CA3AF] font-medium"> / {expected}</span> : ''}
                    </td>
                    <td className={`px-5 py-3 text-center text-[14px] tabular-nums ${awaiting == null ? 'text-[#9CA3AF] font-medium' : `font-bold ${awaitingColor(awaiting, days)}`}`}>
                      {awaiting == null ? '—' : awaiting}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-center">
                        {expected > 0
                          ? <ProgressRing done={registered} total={expected} color={pct >= 100 ? '#1A7A4A' : pct >= 50 ? '#E8A020' : '#C0392B'} size={34} />
                          : <span className="text-[14px] text-[#9CA3AF]">—</span>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
